/* Nightpass — persistance du numéro de TVA saisi au panier.
 *
 * Les deux champs portent `form="cart"` : ils partiraient donc déjà avec le
 * POST du bouton « Commander ». Ce script existe pour les deux cas où ce POST
 * n'a pas lieu :
 *
 *   1. les boutons de paiement accéléré (Shop Pay, PayPal, Google Pay) rendus
 *      par `content_for_additional_checkout_buttons`, qui court-circuitent le
 *      formulaire du panier et emmènent le client directement à la caisse ;
 *   2. un client qui saisit son numéro puis quitte la page — sans écriture
 *      côté serveur, la saisie serait perdue au retour.
 *
 * L'écriture se fait donc sur `change` (donc à la sortie du champ), via
 * `/cart/update.js`. Pas sur `input` : une requête par frappe pour une donnée
 * que personne n'attend en direct.
 */
(() => {
  const ROUTE = (window.routes && window.routes.cart_update_url) || '/cart/update';

  /* Clé de contrôle française : les deux premiers caractères après « FR »
   * valent (12 + 3 x (SIREN mod 97)) mod 97. Un chiffre transposé casse la
   * clé, ce qui est l'erreur de saisie la plus fréquente sur neuf chiffres.
   * Certains numéros portent une clé alphabétique (attribuée aux entreprises
   * étrangères immatriculées en France) : non vérifiable, donc acceptée. */
  const cleFrancaiseValide = (numero) => {
    const m = /^FR([0-9A-Z]{2})(\d{9})$/.exec(numero);
    if (!m) return null;
    if (!/^\d{2}$/.test(m[1])) return null;
    return Number(m[1]) === (12 + 3 * (Number(m[2]) % 97)) % 97;
  };

  /* Deux lettres de pays, puis 2 à 13 caractères : la forme commune aux 27
   * États membres. La longueur exacte varie par pays et n'est pas vérifiée —
   * seule la France l'est vraiment, par sa clé. */
  const FORME = /^[A-Z]{2}[0-9A-Z]{2,13}$/;

  const normaliser = (valeur) => valeur.toUpperCase().replace(/[\s.\-_]/g, '');

  class NpTvaClient extends HTMLElement {
    connectedCallback() {
      this.societe = this.querySelector('#np-tva-societe');
      this.numero = this.querySelector('#np-tva-numero');
      this.etat = this.querySelector('#np-tva-etat');
      if (!this.societe || !this.numero) return;

      this.enVol = null;
      this.addEventListener('change', () => this.enregistrer());
    }

    enregistrer() {
      const numero = normaliser(this.numero.value.trim());
      this.numero.value = numero;

      const societe = this.societe.value.trim();
      this.dire(this.diagnostic(numero));

      /* Une requête chasse la précédente : deux champs quittés coup sur coup
       * ne doivent pas laisser la plus lente écraser la plus récente. */
      const jeton = (this.enVol = Symbol('envoi'));

      fetch(`${ROUTE}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          attributes: {
            'Raison sociale': societe,
            'TVA intracommunautaire': numero,
          },
        }),
      })
        .then((r) => {
          if (!r.ok) throw new Error(r.status);
          return r.json();
        })
        .then(() => {
          if (jeton !== this.enVol) return;
          if (!this.etat.textContent) this.dire('Enregistré.', 'ok');
        })
        .catch(() => {
          if (jeton !== this.enVol) return;
          /* Le POST du formulaire reste le filet : les champs portent
           * `form="cart"` et repartiront avec le bouton « Commander ». On le
           * dit plutôt que d'afficher une erreur qui inquiéterait sans
           * qu'aucune action ne soit attendue du client. */
          this.dire('Sera transmis avec votre commande.', 'ok');
        });
    }

    /* Ne bloque jamais : un numéro douteux est signalé, pas refusé. Le champ
     * est facultatif, et la vérification qui fait foi (VIES) se fait à
     * l'établissement de la facture, pas ici. */
    diagnostic(numero) {
      if (!numero) return '';
      if (!FORME.test(numero)) {
        return 'Format inattendu — un numéro commence par deux lettres de pays, ex. FR12345678901.';
      }
      if (cleFrancaiseValide(numero) === false) {
        return 'Ce numéro français semble comporter une erreur de saisie — vérifiez-le.';
      }
      return '';
    }

    dire(message, ton) {
      if (!this.etat) return;
      this.etat.textContent = message;
      this.etat.classList.toggle('np-tva__etat--alerte', Boolean(message) && ton !== 'ok');
    }
  }

  if (!customElements.get('np-tva-client')) {
    customElements.define('np-tva-client', NpTvaClient);
  }
})();
